import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { initNativeStatusBar } from './lib/statusbar';
import { aterragem, PAPEIS_DO_BOLSO } from './lib/sessao';

// Pages
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminDashboard from './pages/admin/AdminDashboard';
import Resumo from './pages/admin/Resumo';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminLocations from './pages/admin/AdminLocations';
import AdminEmployees from './pages/admin/AdminEmployees';
import AdminTimeRecords from './pages/admin/AdminTimeRecords';
import AdminLeaveRequests from './pages/admin/AdminLeaveRequests';
import AdminVacationMap from './pages/admin/AdminVacationMap';
import AdminHoursReport from './pages/admin/AdminHoursReport';
import AdminDocuments from './pages/admin/AdminDocuments';
import AdminManagers from './pages/admin/AdminManagers';
import AdminSchedules from './pages/admin/AdminSchedules';
import AdminHolidays from './pages/admin/AdminHolidays';
import FinInicio from './pages/admin/financeiro/FinInicio';
import FinPagamentos from './pages/admin/financeiro/FinPagamentos';
import FinVendas from './pages/admin/financeiro/FinVendas';
import FinFornecedores from './pages/admin/financeiro/FinFornecedores';
import FinExtrato from './pages/admin/financeiro/FinExtrato';
import FinConciliacao from './pages/admin/financeiro/conciliacao/FinConciliacao';
import FinRelatorios from './pages/admin/financeiro/FinRelatorios';
import PainelGlobal from './pages/admin/financeiro/PainelGlobal';
import PainelPlataformas from './pages/admin/painel/Plataformas';
import EstoqueFaturas from './pages/admin/estoque/EstoqueFaturas';
import EstoqueStock from './pages/admin/estoque/EstoqueStock';
import EstoqueListaCompras from './pages/admin/estoque/EstoqueListaCompras';
import EstoqueTransferencias from './pages/admin/estoque/EstoqueTransferencias';
import EstoqueHistorico from './pages/admin/estoque/EstoqueHistorico';
import EstoqueFichas from './pages/admin/estoque/EstoqueFichas';
import EstoqueProducao from './pages/admin/estoque/EstoqueProducao';
import EstoqueEmBreve from './pages/admin/estoque/EstoqueEmBreve';
import EstoqueVisaoGeral from './pages/admin/estoque/EstoqueVisaoGeral';
import EstoqueCatalogo from './pages/admin/estoque/EstoqueCatalogo';
import EstoqueCompras from './pages/admin/estoque/EstoqueCompras';
import EstoqueComprasMakro from './pages/admin/estoque/EstoqueComprasMakro';
import EstoqueDefinicoes from './pages/admin/estoque/EstoqueDefinicoes';
import EstoqueUnidades from './pages/admin/estoque/EstoqueUnidades';
import EstoqueUtilizadores from './pages/admin/estoque/EstoqueUtilizadores';
import EstoquePessoas from './pages/admin/estoque/EstoquePessoas';
import ComingSoon from './components/ComingSoon';
import {
  FileText,
  Percent,
  Users,
  BarChart3,
  Truck,
  Banknote,
  Store,
} from 'lucide-react';
import FatDashboard from './pages/admin/faturacao/FatDashboard';
import FatProdutos from './pages/admin/faturacao/FatProdutos';
import FatCategorias from './pages/admin/faturacao/FatCategorias';
import FatDocumentos from './pages/admin/faturacao/FatDocumentos';
import FatClientes from './pages/admin/faturacao/FatClientes';
import FatRelatorios from './pages/admin/faturacao/FatRelatorios';
import FatConsumo from './pages/admin/faturacao/FatConsumo';
import FatDescontoStock from './pages/admin/faturacao/FatDescontoStock';
import FatDeposito from '@/pages/admin/faturacao/FatDeposito';
import FatPersonalizacoes from './pages/admin/faturacao/FatPersonalizacoes';
import FatLojas from './pages/admin/faturacao/FatLojas';
import FatPagamentos from './pages/admin/faturacao/FatPagamentos';
import FatUtilizadores from './pages/admin/faturacao/FatUtilizadores';
import FatMotivos from './pages/admin/faturacao/FatMotivos';
import FatDispositivos from './pages/admin/faturacao/FatDispositivos';
import FatRelatorioDiario from './pages/admin/faturacao/FatRelatorioDiario';
import FatTrocarModo from './pages/admin/faturacao/FatTrocarModo';
import FatMovimentosCaixa from './pages/admin/faturacao/FatMovimentosCaixa';
import FatReservasPresas from './pages/admin/faturacao/FatReservasPresas';
import PosApp from './pages/pos/PosApp';
import BolsoApp from './pages/bolso/BolsoApp';
import Barreira from './pages/bolso/Barreira';
import { Button } from './components/ui/button';
import BolsoCalendario from './pages/bolso/BolsoCalendario';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import EmployeeProfile from './pages/employee/EmployeeProfile';
import EmployeeTimeRecord from './pages/employee/EmployeeTimeRecord';
import EmployeeLeaveRequests from './pages/employee/EmployeeLeaveRequests';
import EmployeeDocuments from './pages/employee/EmployeeDocuments';

// Layout
import AdminLayout from './components/layouts/AdminLayout';
import EmployeeLayout from './components/layouts/EmployeeLayout';
import CrmLayout from './components/layouts/CrmLayout';
import { filhosCrm, rotasCrmNoAdmin } from './pages/crm/rotasCrm';
import MarketingCampaigns from './pages/admin/marketing/MarketingCampaigns';
import MarketingCalendar from './pages/admin/marketing/MarketingCalendar';
import MarketingReviews from './pages/admin/marketing/MarketingReviews';
import MarketingReports from './pages/admin/marketing/MarketingReports';

// O arranque não conseguiu perguntar ao servidor se a sessão vale (rede em
// baixo, 502 de um deploy). O token ficou: mandar para o login era pedir a
// senha por causa de um pedido que nunca foi respondido. Mostrado pelo
// `ProtectedRoute` e pelo `RoleRedirect` (a rota `/` do portal no browser).
const SemLigacao = () => {
  const { tentarDeNovo } = useAuth();
  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-2 bg-background px-6 text-center">
      <p className="text-base font-semibold">Sem ligação</p>
      <p className="text-sm text-muted-foreground">Não foi possível falar com o servidor.</p>
      <Button className="mt-3 h-11 px-6" onClick={tentarDeNovo}>Tentar de novo</Button>
    </div>
  );
};

// Protected Route Component - checks for authentication and must_change_password
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated, mustChangePassword, semLigacao } = useAuth();
  // De ONDE veio quem foi mandado para o login. Sem isto, quem abre a app da
  // Gestão Lisbonb (que arranca em /bolso), entra, e é despejado no
  // /admin/resumo — o portal inteiro num ecrã de telemóvel, que não é nada do
  // que ele foi lá ver. Ver `aterragem` (lib/sessao.js) e o `LoginPage`.
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated && semLigacao) return <SemLigacao />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  
  // Redirect to change password if required
  if (mustChangePassword) {
    return <Navigate to="/alterar-senha" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={['admin', 'gerente', 'contabilista'].includes(user.role) ? '/admin' : '/colaborador'} replace />;
  }
  
  return children;
};

// As rotas da app «Gestão Lisbonb»: o login do portal e, na casca com Face ID,
// a Barreira. **TODAS as rotas /bolso passam por aqui**: com o Face ID ligado,
// os pedidos do `lib/bolso.js` esperam que a Barreira abra — uma rota /bolso
// sem ela ficava à espera para sempre (guardado em test_sessao_do_bolso.py).
const RotaDoBolso = ({ children }) => (
  <ProtectedRoute allowedRoles={PAPEIS_DO_BOLSO}>
    <Barreira>{children}</Barreira>
  </ProtectedRoute>
);

// Change Password Route - only accessible when must_change_password is true
const ChangePasswordRoute = ({ children }) => {
  const { user, loading, isAuthenticated, mustChangePassword } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If user doesn't need to change password, redirect to appropriate dashboard
  if (!mustChangePassword) {
    return <Navigate to={aterragem(user.role)} replace />;
  }
  
  return children;
};

// Redirect based on role
const RoleRedirect = () => {
  const { user, loading, isAuthenticated, mustChangePassword, semLigacao } = useAuth();
  // Antes do `loading` sem perigo: o `semLigacao` só fica de pé com o `loading` já em baixo.
  if (!isAuthenticated && semLigacao) return <SemLigacao />;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to change password if required
  if (mustChangePassword) {
    return <Navigate to="/alterar-senha" replace />;
  }
  
  // Na casca com Face ID, um gestor aterra no /bolso (dentro da Barreira); no
  // browser, onde sempre aterrou. Ver `aterragem` em lib/sessao.js.
  return <Navigate to={aterragem(user.role)} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/privacidade" element={<PrivacyPolicy />} />

      {/* Ponto de Venda: rota de topo, fora do /admin e sem sessão de
          backoffice — as lojas usam só este link, com entrada por PIN,
          nunca o login do portal. Ver AdminLayout: o botão "Iniciar Ponto
          de Venda" abre este caminho num separador novo. PosApp é o shell
          (emparelhamento -> entrada -> caixa -> venda); os dois tokens do
          POS vivem em localStorage (lib/pos.js), nunca no Authorization do
          backoffice. */}
      <Route path="/faturacao/pos" element={<PosApp />} />

      {/* Gestão Lisbonb (interno: "bolso") — a faturação no telemóvel do gestor. Rota de TOPO,
          fora do /admin e com layout próprio: o AdminLayout é uma gaveta de
          seis secções pensada para um ecrã grande, e o que aqui se quer é o
          número a zero toques. Leva `ProtectedRoute` na mesma (é o login do
          portal que manda), e o que cada um vê continua a ser decidido pela
          PERTENÇA às empresas, do lado do servidor. */}
      <Route path="/bolso" element={<RotaDoBolso><BolsoApp /></RotaDoBolso>} />
      {/* O calendário das lojas (onde falta gente), irmão do /bolso: mesmos papéis, mesma
          casca. O contabilista lê; quem marca é decidido pelo servidor (`pode_marcar`). */}
      <Route path="/bolso/calendario" element={<RotaDoBolso><BolsoCalendario /></RotaDoBolso>} />
      {/* CRM Purple House: área própria só para membros (guarda no CrmLayout; rotas em pages/crm/rotasCrm.js). */}
      <Route path="/crm" element={<ProtectedRoute><CrmLayout /></ProtectedRoute>}>{filhosCrm()}</Route>
      <Route path="/alterar-senha" element={
        <ChangePasswordRoute>
          <ChangePasswordPage />
        </ChangePasswordRoute>
      } />
      <Route path="/" element={<RoleRedirect />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin', 'gerente', 'contabilista']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="resumo" element={<Resumo />} />
        <Route path="painel" element={<PainelGlobal />} />
        <Route path="painel/plataformas" element={<PainelPlataformas />} />
        <Route path="empresas" element={<AdminCompanies />} />
        <Route path="locais" element={<AdminLocations />} />
        <Route path="colaboradores" element={<AdminEmployees />} />
        <Route path="ponto" element={<AdminTimeRecords />} />
        <Route path="relatorio-horas" element={<AdminHoursReport />} />
        <Route path="ausencias" element={<AdminLeaveRequests />} />
        <Route path="mapa-ferias" element={<AdminVacationMap />} />
        <Route path="feriados" element={<AdminHolidays />} />
        <Route path="documentos" element={<AdminDocuments />} />
        <Route path="gestores" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminManagers />
          </ProtectedRoute>
        } />
        <Route path="escalas" element={<AdminSchedules />} />

        {/* ===== Financeiro ===== */}
        {/* Fase 2: Início (empresas+unidades) e Equipa já implementados. */}
        {/* Início deixou de existir: aterra em Vendas. Empresas/lojas movidas
            para Configurações (botão de engrenagem no topo). */}
        <Route path="financeiro" element={<Navigate to="/admin/financeiro/vendas" replace />} />
        <Route path="financeiro/configuracoes" element={<FinInicio />} />
        <Route path="financeiro/pagamentos" element={<FinPagamentos />} />
        <Route path="financeiro/vendas" element={<FinVendas />} />
        <Route path="financeiro/relatorios" element={<FinRelatorios />} />
        <Route path="financeiro/fornecedores" element={<FinFornecedores />} />
        <Route path="financeiro/extrato" element={<FinExtrato />} />
        <Route path="financeiro/conciliacao" element={<FinConciliacao />} />

        {/* ===== Estoque ===== */}
        <Route path="estoque" element={<Navigate to="/admin/estoque/visao-geral" replace />} />
        <Route path="estoque/visao-geral" element={<EstoqueVisaoGeral />} />
        <Route path="estoque/stock" element={<EstoqueStock />} />
        <Route path="estoque/catalogo" element={<EstoqueCatalogo />} />
        <Route path="estoque/escanear" element={<EstoqueEmBreve />} />
        <Route path="estoque/lista-compras" element={<EstoqueListaCompras />} />
        <Route path="estoque/compras" element={<EstoqueCompras />} />
        <Route path="estoque/compras-makro" element={<EstoqueComprasMakro />} />
        <Route path="estoque/transferencias" element={<EstoqueTransferencias />} />
        <Route path="estoque/fichas" element={<EstoqueFichas />} />
        <Route path="estoque/producao" element={<EstoqueProducao />} />
        <Route path="estoque/faturas" element={<EstoqueFaturas />} />
        <Route path="estoque/definicoes" element={<EstoqueDefinicoes />} />
        <Route path="estoque/unidades" element={<EstoqueUnidades />} />
        <Route path="estoque/utilizadores" element={<EstoqueUtilizadores />} />
        <Route path="estoque/pessoas" element={<EstoquePessoas />} />
        <Route path="estoque/historico" element={<EstoqueHistorico />} />

        {/* ===== Faturação ===== */}
        <Route path="faturacao" element={<Navigate to="/admin/faturacao/dashboard" replace />} />

        {/* Gestão — a estrutura pedida pelo dono, decalcada do backoffice do
            Vendus. O que ainda não está construído mostra "Brevemente", como o
            Financeiro fez enquanto crescia. */}
        <Route path="faturacao/dashboard" element={<FatDashboard />} />
        <Route path="faturacao/produtos" element={<Navigate to="/admin/faturacao/produtos/lista" replace />} />
        <Route path="faturacao/produtos/lista" element={<FatProdutos />} />
        <Route path="faturacao/produtos/categorias" element={<FatCategorias />} />
        <Route path="faturacao/produtos/personalizacoes" element={<FatPersonalizacoes />} />
        <Route path="faturacao/produtos/deposito" element={<FatDeposito />} />
        <Route path="faturacao/documentos" element={<FatDocumentos />} />
        {/* A lista que as mensagens do POS já mandavam o gestor consultar
            ("chame o gestor, que resolve na lista de reservas fiscais presas")
            e que não existia — sem ela, uma emissão que fica a meio tranca a
            conta e impede a loja de fechar a caixa. */}
        <Route path="faturacao/reservas-presas" element={<FatReservasPresas />} />
        <Route path="faturacao/taloes-desconto" element={<ComingSoon icon={Percent} title="Talões de Desconto" subtitle="Faturação · Gestão" />} />
        <Route path="faturacao/clientes" element={<FatClientes />} />
        <Route path="faturacao/relatorios" element={<FatRelatorios />} />
        <Route path="faturacao/consumo" element={<FatConsumo />} />
        <Route path="faturacao/compras" element={<ComingSoon icon={Truck} title="Compras" subtitle="Faturação · Gestão" note="Entrada de mercadoria a partir das faturas de compra que já existem no Financeiro." />} />

        {/* POS — o "Iniciar Ponto de Venda" já não vive aqui: é o botão do
            topo do painel, e abre /faturacao/pos (fora do /admin) num
            separador novo. Ver App.js, rotas de topo, e AdminLayout. */}
        <Route path="faturacao/movimentos-caixa" element={<FatMovimentosCaixa />} />
        <Route path="faturacao/pos-lojas" element={<ComingSoon icon={Store} title="Lojas" subtitle="Faturação · POS" note="Definições de cada loja e o que sai impresso no talão do cliente." />} />

        {/* Configuração — os ecrãs que já funcionam. A Configuração pendura-se
            no carril como um item com filhos (ver AdminLayout); precisa de
            um caminho próprio para o pai poder navegar antes de expandir. */}
        <Route path="faturacao/config" element={<Navigate to="/admin/faturacao/config/lojas" replace />} />
        <Route path="faturacao/config/lojas" element={<FatLojas />} />
        <Route path="faturacao/config/pagamentos" element={<FatPagamentos />} />
        <Route path="faturacao/config/utilizadores" element={<FatUtilizadores />} />
        <Route path="faturacao/config/motivos" element={<FatMotivos />} />
        <Route path="faturacao/config/dispositivos" element={<FatDispositivos />} />
        <Route path="faturacao/config/relatorio-diario" element={<FatRelatorioDiario />} />
        <Route path="faturacao/config/modo-de-emissao" element={<FatTrocarModo />} />
        <Route path="faturacao/config/desconto-stock" element={<FatDescontoStock />} />

        {/* Os caminhos antigos, de antes da estrutura por secções */}
        <Route path="faturacao/lojas" element={<Navigate to="/admin/faturacao/config/lojas" replace />} />
        <Route path="faturacao/pagamentos" element={<Navigate to="/admin/faturacao/config/pagamentos" replace />} />
        <Route path="faturacao/utilizadores" element={<Navigate to="/admin/faturacao/config/utilizadores" replace />} />
        <Route path="faturacao/motivos" element={<Navigate to="/admin/faturacao/config/motivos" replace />} />

        {/* ===== Marketing ===== */}
        <Route path="marketing" element={<MarketingCampaigns />} />
        <Route path="marketing/calendario" element={<MarketingCalendar />} />
        <Route path="marketing/avaliacoes" element={<MarketingReviews />} />
        <Route path="marketing/relatorios" element={<MarketingReports />} />

        {/* ===== CRM ===== (as mesmas páginas da área /crm, com a GuardaCrm) */}
        {rotasCrmNoAdmin()}
      </Route>
      
      {/* Employee Routes */}
      <Route path="/colaborador" element={
        <ProtectedRoute allowedRoles={['colaborador']}>
          <EmployeeLayout />
        </ProtectedRoute>
      }>
        <Route index element={<EmployeeDashboard />} />
        <Route path="perfil" element={<EmployeeProfile />} />
        <Route path="ponto" element={<EmployeeTimeRecord />} />
        <Route path="ausencias" element={<EmployeeLeaveRequests />} />
        <Route path="documentos" element={<EmployeeDocuments />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Barra de estado nativa a acompanhar o tema (app Android/iOS).
    initNativeStatusBar();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
